/**
 * 사용자 속성 정의 관리 — `user_attribute_definitions` (회사 차원의 attribute 스키마).
 *
 * 어드민이 회사 모델에 맞춰 사용자 속성 (attribute) 을 자유롭게 정의 (key/label/type/options).
 * 정의된 속성은 다음에 모두 활용:
 *   - 사용자 편집 페이지의 입력 필드 (자동 생성)
 *   - 그룹 자동 부여 룰 (`role_attribute_grants`)
 *   - 데이터소스 scope rule (`data_source_table_scope_rules`)
 *   - 외부 동기화 매핑 (`SyncFieldMapping`)
 *
 * 권한 인프라의 1급 시민이라 별도 페이지로 분리. 정의 자체는 회사 전체에 1번, 각 사용자의
 * 값은 사용자 편집 페이지에서 입력.
 */
import { AttributeFormModal } from '@/components/admin/UserAttributeManagerModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { UserAttributeDefinition, UserAttributeType } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

const TYPE_LABEL: Record<UserAttributeType, string> = {
  text: '텍스트',
  number: '숫자',
  date: '날짜',
  boolean: '예/아니오',
  enum: '선택지',
};

export function AdminPermissionsAttributes() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'user-attributes'],
    queryFn: api.adminUserAttributesList,
  });
  const defs = data?.definitions ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UserAttributeDefinition | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.adminReorderUserAttributes(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-attributes'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : '순서 변경에 실패했습니다.'),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.adminDeleteUserAttribute(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-attributes'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : '삭제에 실패했습니다.'),
  });

  const toggleDirectory = useMutation({
    mutationFn: ({ id, showInDirectory }: { id: string; showInDirectory: boolean }) =>
      api.adminUpdateUserAttribute(id, { showInDirectory }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'user-attributes'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : '노출 설정 변경에 실패했습니다.'),
  });

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...defs];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    const a = next[idx];
    const b = next[target];
    if (!a || !b) return;
    next[idx] = b;
    next[target] = a;
    reorder.mutate(next.map((d) => d.id));
  };

  return (
    <PageShell
      header={
        <PageHeader
          title="사용자 속성 정의"
          count={defs.length}
          description="직책·부서·생년월일 같은 사용자 속성을 등록합니다. 회사 차원의 스키마 1번 정의 — 각 사용자의 값은 사용자 편집 페이지에서 입력. 정의한 속성은 그룹 자동 부여 룰·데이터소스 스코프 룰·외부 동기화 매핑에서 공통 참조됩니다. 눈 아이콘으로 켠 속성은 일반 사용자가 공유·그룹 화면에서 사람을 검색할 때 함께 표시됩니다 (동명이인 구분)."
          actions={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> 속성 추가
            </Button>
          }
        />
      }
    >
      {error ? (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : defs.length === 0 ? (
        <div className="rounded-md border border-border bg-surface-muted/40 px-3 py-6 text-center text-sm text-foreground-muted">
          아직 등록된 속성이 없습니다. 우상단 "속성 추가" 로 시작하세요.
        </div>
      ) : (
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted/40 text-xs uppercase tracking-wider text-foreground-muted">
              <tr>
                <th className="px-3 py-2 w-20">순서</th>
                <th className="px-3 py-2 text-left">라벨</th>
                <th className="px-3 py-2 text-left">key</th>
                <th className="px-3 py-2 text-left">타입</th>
                <th className="px-3 py-2 text-left">필수</th>
                <th className="px-3 py-2 text-left">검색 노출</th>
                <th className="px-3 py-2 text-right">액션</th>
              </tr>
            </thead>
            <tbody>
              {defs.map((d, i) => (
                <tr key={d.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <div className="inline-flex items-center gap-1">
                      <IconBtn aria-label="위로" onClick={() => move(i, -1)} disabled={i === 0}>
                        <ArrowUp className="size-3.5" />
                      </IconBtn>
                      <IconBtn
                        aria-label="아래로"
                        onClick={() => move(i, 1)}
                        disabled={i === defs.length - 1}
                      >
                        <ArrowDown className="size-3.5" />
                      </IconBtn>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-medium">{d.label}</td>
                  <td className="px-3 py-2 font-mono text-xs">{d.key}</td>
                  <td className="px-3 py-2">
                    {TYPE_LABEL[d.type]}
                    {d.type === 'enum' && d.options ? (
                      <span className="ml-2 text-xs text-foreground-muted">
                        ({d.options.length}개)
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{d.required ? '필수' : '선택'}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      aria-label={d.showInDirectory ? '검색 노출 끄기' : '검색 노출 켜기'}
                      title={
                        d.showInDirectory
                          ? '공유/그룹 검색 결과에 표시됨 — 클릭하여 숨김'
                          : '숨김 — 클릭하여 공유/그룹 검색 결과에 표시'
                      }
                      onClick={() =>
                        toggleDirectory.mutate({ id: d.id, showInDirectory: !d.showInDirectory })
                      }
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs',
                        d.showInDirectory
                          ? 'text-accent hover:bg-surface-muted'
                          : 'text-foreground-muted hover:bg-surface-muted',
                      )}
                    >
                      {d.showInDirectory ? (
                        <Eye className="size-3.5" />
                      ) : (
                        <EyeOff className="size-3.5" />
                      )}
                      {d.showInDirectory ? '표시' : '숨김'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <IconBtn aria-label="편집" onClick={() => setEditing(d)}>
                      <Pencil className="size-3.5" />
                    </IconBtn>
                    <IconBtn
                      aria-label="삭제"
                      danger
                      onClick={() => {
                        if (
                          window.confirm(
                            `'${d.label}' 속성을 삭제하시겠습니까? (기존 사용자 값은 보존)`,
                          )
                        ) {
                          del.mutate(d.id);
                        }
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </IconBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen ? (
        <AttributeFormModal mode="create" onClose={() => setCreateOpen(false)} />
      ) : null}
      {editing ? (
        <AttributeFormModal mode="edit" def={editing} onClose={() => setEditing(null)} />
      ) : null}
    </PageShell>
  );
}

function IconBtn({
  children,
  onClick,
  disabled,
  danger,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
} & React.AriaAttributes) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...rest}
      className={cn(
        'size-7 inline-grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        danger ? 'hover:text-destructive' : '',
      )}
    >
      {children}
    </button>
  );
}
